import React from 'react';
import PropTypes from 'prop-types';
import { Box, Divider, selectSystemProps, Typography, useCopy, useThemeTokens, useViewport, getTokensPropType, useTheme, useResponsiveThemeTokens, createMediaQueryStyles, StyleSheet } from '@telus-uds/components-base';
import styled from 'styled-components';
import ExpandCollapse from './ExpandCollapse';
import OrderedListBase from '../OrderedList/OrderedListBase';
import { htmlAttrs, media, renderStructuredContent } from '../utils';
import defaultDictionary from './dictionary';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const [selectProps, selectedSystemPropTypes] = selectSystemProps([htmlAttrs]);
const ContentContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "TermsAndConditions__ContentContainer",
  componentId: "components-web__sc-1199epn-0"
})(_ref => {
  let {
    tokens
  } = _ref;
  return {
    paddingBottom: tokens.contentPaddingBottom,
    paddingLeft: tokens.contentPaddingLeft,
    ...media().from('md').css({
      paddingBottom: tokens.mdContentPaddingBottom,
      paddingLeft: tokens.mdContentPaddingLeft
    })
  };
});
const Ordered = /*#__PURE__*/styled(OrderedListBase).withConfig({
  displayName: "TermsAndConditions__Ordered",
  componentId: "components-web__sc-1199epn-1"
})(_ref2 => {
  let {
    tokens
  } = _ref2;
  return {
    listStylePosition: 'outside',
    padding: tokens.orderedPadding
  };
});
const Unordered = /*#__PURE__*/styled.ul.withConfig({
  displayName: "TermsAndConditions__Unordered",
  componentId: "components-web__sc-1199epn-2"
})(_ref3 => {
  let {
    tokens
  } = _ref3;
  return {
    listStyleType: 'none',
    margin: 0,
    padding: 0,
    paddingTop: tokens.unorderedPadding
  };
});
const ListItem = /*#__PURE__*/styled(OrderedListBase.Item).withConfig({
  displayName: "TermsAndConditions__ListItem",
  componentId: "components-web__sc-1199epn-3"
})(_ref4 => {
  let {
    tokens
  } = _ref4;
  return {
    display: 'list-item',
    '&::marker': {
      fontFamily: `${tokens.listFontName}${tokens.listFontWeight}normal`,
      fontSize: tokens.listFontSize,
      lineHeight: tokens.listLineHeight,
      textAlign: 'end !important'
    },
    color: tokens.listColor,
    fontFamily: `${tokens.listFontName}${tokens.listFontWeight}normal`,
    fontSize: tokens.listFontSize,
    lineHeight: tokens.listLineHeight,
    marginBottom: tokens.listMarginBottom,
    marginLeft: tokens.listMarginLeft,
    wordBreak: 'break-word'
  };
});
const NonIndexedContentTitle = /*#__PURE__*/styled.div.withConfig({
  displayName: "TermsAndConditions__NonIndexedContentTitle",
  componentId: "components-web__sc-1199epn-4"
})(_ref5 => {
  let {
    tokens
  } = _ref5;
  return {
    color: tokens.titleColor,
    fontSize: tokens.titleFontSize,
    lineHeight: tokens.titleLineHeight,
    paddingLeft: tokens.titlePaddingLeft
  };
});

/**
 * Use `TermsAndConditions` to display important legal content.
 *
 * ## Usage Criteria
 *
 * - Display before the page footer, except for call-to-action callback cards
 * - Responsive display based on breakpoints
 * - Use `copy` to set language, ‘en’ for English or ‘fr’ for French
 */
const TermsAndConditions = /*#__PURE__*/React.forwardRef((_ref6, ref) => {
  let {
    copy = 'en',
    indexedContent = [],
    nonIndexedContent = [],
    tokens = {},
    variant = {},
    dictionary = defaultDictionary,
    ...rest
  } = _ref6;
  const getCopy = useCopy({
    dictionary,
    copy
  });
  const hasIndexedContent = indexedContent.length > 0;
  const hasNonIndexedContent = nonIndexedContent.length > 0;
  const viewport = useViewport();
  const {
    themeOptions
  } = useTheme();
  const {
    enableMediaQueryStyleSheet
  } = themeOptions;
  const useTokens = enableMediaQueryStyleSheet ? useResponsiveThemeTokens : useThemeTokens;
  const themeTokens = useTokens('TermsAndConditions', tokens, variant, !enableMediaQueryStyleSheet && {
    viewport
  });
  let listItemStyles;
  let listItemMediaIds;
  let nonIndexedContentStyles;
  let nonIndexedContentMediaIds;
  if (enableMediaQueryStyleSheet) {
    const {
      transformedListItemThemeTokens,
      transformedNonIndexedContentThemeTokens
    } = Object.entries(themeTokens).reduce((acc, _ref7) => {
      let [vp, viewportTokens] = _ref7;
      acc.transformedListItemThemeTokens[vp] = {
        marginLeft: viewportTokens.listMarginLeft
      };
      acc.transformedNonIndexedContentThemeTokens[vp] = {
        paddingLeft: viewportTokens.titlePaddingLeft
      };
      return acc;
    }, {
      transformedListItemThemeTokens: {},
      transformedNonIndexedContentThemeTokens: {}
    });
    const listItemMediaQueryStyles = createMediaQueryStyles(transformedListItemThemeTokens);
    const nonIndexedContentMediaQueryStyles = createMediaQueryStyles(transformedNonIndexedContentThemeTokens);
    const {
      ids,
      styles
    } = StyleSheet.create({
      listItem: {
        ...themeTokens[viewport],
        ...listItemMediaQueryStyles
      },
      nonIndexedContent: {
        ...themeTokens[viewport],
        ...nonIndexedContentMediaQueryStyles
      }
    });
    listItemStyles = styles.listItem;
    listItemMediaIds = ids.listItem;
    nonIndexedContentStyles = styles.nonIndexedContent;
    nonIndexedContentMediaIds = ids.nonIndexedContent;
  } else {
    listItemStyles = themeTokens;
    nonIndexedContentStyles = themeTokens;
  }
  return /*#__PURE__*/_jsxs("div", {
    ...selectProps(rest),
    children: [/*#__PURE__*/_jsx(Divider, {
      tokens: {
        color: themeTokens.dividerColor
      }
    }), /*#__PURE__*/_jsx(ExpandCollapse, {
      collapseTitle: getCopy('headingView'),
      expandTitle: getCopy('headingHide'),
      collapseLabel: getCopy('collapseLabel'),
      expandLabel: getCopy('expandLabel'),
      ref: ref,
      children: /*#__PURE__*/_jsxs(ContentContainer, {
        tokens: themeTokens,
        children: [hasIndexedContent && /*#__PURE__*/_jsx(Ordered, {
          tokens: themeTokens,
          children: indexedContent.map((contentItem, idx) =>
          /*#__PURE__*/
          // eslint-disable-next-line react/no-array-index-key
          _jsx(ListItem, {
            tokens: listItemStyles,
            media: listItemMediaIds,
            children: renderStructuredContent(contentItem)
          }, idx))
        }), hasNonIndexedContent && /*#__PURE__*/_jsxs(Box, {
          between: 3,
          children: [/*#__PURE__*/_jsx(NonIndexedContentTitle, {
            tokens: nonIndexedContentStyles,
            media: nonIndexedContentMediaIds,
            children: /*#__PURE__*/_jsx(Typography, {
              block: true,
              heading: true,
              tokens: {
                fontName: themeTokens.expandTitleFontName,
                fontWeight: themeTokens.expandTitleFontWeight,
                fontSize: themeTokens.titleFontSize,
                lineHeight: themeTokens.titleLineHeight
              },
              children: getCopy('nonIndexedTitle')
            })
          }), /*#__PURE__*/_jsx(Unordered, {
            tokens: themeTokens,
            children: nonIndexedContent.map((contentItem, idx) =>
            /*#__PURE__*/
            // eslint-disable-next-line react/no-array-index-key
            _jsx(ListItem, {
              tokens: listItemStyles,
              media: listItemMediaIds,
              children: renderStructuredContent(contentItem)
            }, idx))
          })]
        })]
      })
    }), /*#__PURE__*/_jsx(Divider, {
      tokens: {
        color: themeTokens.dividerColor
      }
    })]
  });
});
TermsAndConditions.displayName = 'TermsAndConditions';

// If a language dictionary entry is provided, it must contain every key
const dictionaryContentShape = PropTypes.shape({
  headingHide: PropTypes.string.isRequired,
  headingView: PropTypes.string.isRequired,
  nonIndexedTitle: PropTypes.string.isRequired
});
TermsAndConditions.propTypes = {
  ...selectedSystemPropTypes,
  /**
   * Use the `copy` prop to either select provided English or French copy by passing 'en' or 'fr' respectively.
   *
   * To provide your own, pass a JSON object with the keys `headingView`, `headingHide`, and `nonIndexedTitle`.
   */
  copy: PropTypes.oneOfType([PropTypes.oneOf(['en', 'fr']), PropTypes.shape({
    headingView: PropTypes.string,
    headingHide: PropTypes.string,
    nonIndexedTitle: PropTypes.string
  })]),
  /**
   * Sets the tokens for TermsAndConditions element.
   */
  tokens: getTokensPropType('TermsAndConditions'),
  /**
   * An array of nodes, strings, or a combination to be displayed in an ordered list.
   *
   * Each item in the array must have a corresponding superscript in the page.
   */
  indexedContent: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.node, PropTypes.string])),
  /**
   * An array of nodes, strings, or a combination to be displayed in an unordered list.
   *
   * nonIndexedContent do not have a corresponding superscript and instead apply to the page as a whole.
   */
  nonIndexedContent: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.node, PropTypes.string])),
  /**
   * Custom dictionary containing the labels to use for `TermsAndConditions`
   */
  dictionary: PropTypes.shape({
    en: dictionaryContentShape,
    fr: dictionaryContentShape
  })
};
export default TermsAndConditions;